import { mdiAccountOutline, mdiEmailOutline, mdiPhoneOutline } from "@mdi/js"
import type {
  InboxWorkflowItem,
  InboxWorkflowRowOutcomeMap,
  InboxWorkflowState,
  InboxWorkflowStep,
} from "@kontextwerk/control-web-frame/inbox-workflow/contract"
import {
  buildRichTooltip,
  tooltipField,
  tooltipTitle,
} from "../../../../../../../../../../../../../_shared/_logic/rich-tooltips"
import { workflowStatusFromRowState } from "../../../../../_shared/_logic/result-outcomes"
import { inboxAppointmentTimeFromPayload, inboxScheduleLabelFromPayload } from "./scheduleTags"
import {
  workflowItemHasRelevantStep,
  workflowItemNeedsStepFor,
  workflowItemOutputFor,
  workflowItemStepFor,
  workflowItemStepResolvedFor,
} from "../../../_shared/_logic/table-rows"
import type {
  AssignWorkOrderPreviewPayload,
  AssignWorkOrderPreviewRowContext,
  OrderTypePreviewSummary,
  PropertyPreviewSummary,
  ScheduleEventPreviewSummary,
  WorkOrderAssignmentCandidate,
  WorkOrderAssignmentContextTag,
  WorkOrderAssignmentRequesterContactMethod,
  WorkOrderAssignmentRow,
  WorkOrderAssignmentSelection,
  WorkOrderPreviewSummary,
} from "../contract"

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown): JsonObject =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {}

const objectArray = (value: unknown): JsonObject[] =>
  Array.isArray(value) ? value.map(objectValue) : []

const stringValue = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

const roleLabel = (value: unknown) => {
  const role = stringValue(value).toUpperCase()
  const labels: Record<string, string> = {
    TENANT: "Mieter",
    OWNER: "Eigentümer",
    MANAGER: "Verwaltung",
    PROPERTY_MANAGER: "Verwaltung",
    ONSITE_CONTACT: "Vor-Ort-Kontakt",
    BILLING_CONTACT: "Rechnungskontakt",
    OTHER: "Sonstige",
  }

  return labels[role] ?? stringValue(value)
}

const itemIsRelevant = (
  item: InboxWorkflowItem,
  workflow: InboxWorkflowState,
  step: InboxWorkflowStep,
) => item.action_case.flow_key === workflow.flow.flow_key && workflowItemHasRelevantStep(item, step)

const orderTypeItemsFor = (
  workOrder: WorkOrderPreviewSummary,
  preview: AssignWorkOrderPreviewPayload,
): WorkOrderOrderTypeItem[] => {
  if (workOrder.orderTypeIds.length) {
    return workOrder.orderTypeIds.map((id) => {
      const orderType = preview.orderTypesById[id]
      const fallbackLabel = workOrder.orderTypeLabel || id

      return {
        order_type_id: id,
        internal_label: orderType?.internalLabel || fallbackLabel,
        public_label: orderType?.publicLabel || orderType?.internalLabel || fallbackLabel,
        description: orderType?.description || null,
      }
    })
  }

  return workOrder.orderTypeLabel
    ? [
        {
          order_type_id: null,
          internal_label: workOrder.orderTypeLabel,
          public_label: workOrder.orderTypeLabel,
          description: null,
        },
      ]
    : []
}

const orderTypeLabelFor = (
  workOrder: WorkOrderPreviewSummary,
  preview: AssignWorkOrderPreviewPayload,
): string => {
  const items = orderTypeItemsFor(workOrder, preview)
  const joined = items
    .map((item) => item.internal_label.trim())
    .filter(Boolean)
    .join(", ")

  return joined || workOrder.orderTypeLabel
}

const propertyFor = (
  propertyId: string | null,
  preview: AssignWorkOrderPreviewPayload,
): PropertyPreviewSummary | null =>
  propertyId ? (preview.propertiesById[propertyId] ?? null) : null

const scheduleEventFor = (
  scheduleEventId: string | null,
  preview: AssignWorkOrderPreviewPayload,
): ScheduleEventPreviewSummary | null =>
  scheduleEventId ? (preview.scheduleEventsById[scheduleEventId] ?? null) : null

const scheduleEventView = (event: ScheduleEventPreviewSummary | null): ScheduleEvent | null => {
  if (!event) return null

  return {
    id: event.id,
    work_order_id: event.workOrderId,
    property_id: event.propertyId,
    employee_id: event.employeeId,
    employee_name: event.employeeName || null,
    start_at: event.startAt,
    end_at: event.endAt,
    timeframe_start_at: event.timeframeStartAt,
    timeframe_end_at: event.timeframeEndAt,
    customer_window_start_at: event.customerWindowStartAt,
    customer_window_end_at: event.customerWindowEndAt,
    status: event.status,
    flex_type: event.flexType ?? "HARD",
  } as ScheduleEvent
}

const workOrderView = (
  workOrder: WorkOrderPreviewSummary,
  preview: AssignWorkOrderPreviewPayload,
): WorkOrder =>
  ({
    id: workOrder.id,
    property_id: workOrder.propertyId,
    property_unit_id: workOrder.propertyUnitId,
    property_unit_label: workOrder.propertyUnitLabel || null,
    order_number: workOrder.orderNumber || null,
    order_types: orderTypeItemsFor(workOrder, preview),
    due_date: workOrder.dueDate,
    estimated_duration_min: workOrder.estimatedDurationMin,
    assigned_employee_id: workOrder.assignedEmployeeId,
    allowed_employee_ids: workOrder.allowedEmployeeIds,
    current_schedule_event_id: workOrder.currentScheduleEventId,
    status: workOrder.status,
  }) as unknown as WorkOrder

const candidateFor = (
  workOrder: WorkOrderPreviewSummary,
  preview: AssignWorkOrderPreviewPayload,
): WorkOrderAssignmentCandidate => {
  const property = propertyFor(workOrder.propertyId, preview)
  const currentEvent = scheduleEventFor(workOrder.currentScheduleEventId, preview)
  const orderTypeLabel = orderTypeLabelFor(workOrder, preview)
  const orderNumberLabel = workOrder.orderNumber ? `#${workOrder.orderNumber}` : ""
  const label = [orderTypeLabel || "Auftrag", orderNumberLabel].filter(Boolean).join(" ")
  const selection: WorkOrderAssignmentSelection = {
    id: workOrder.id,
    label,
    workOrder: workOrderView(workOrder, preview),
    currentScheduleEvent: scheduleEventView(currentEvent),
    propertyId: workOrder.propertyId,
    propertyUnitId: workOrder.propertyUnitId,
    propertyLabel: property?.addressLabel || "Liegenschaft",
    unitLabel: workOrder.propertyUnitLabel,
  }

  return {
    id: workOrder.id,
    label,
    orderNumber: workOrder.orderNumber,
    orderType: orderTypeLabel,
    dueDate: workOrder.dueDate,
    selection,
    propertyId: workOrder.propertyId,
    propertyUnitId: workOrder.propertyUnitId,
    propertyLabel: property?.addressLabel || "",
    unitLabel: workOrder.propertyUnitLabel,
    statusLabel: workOrder.status,
    durationLabel: workOrder.estimatedDurationMin ? `${workOrder.estimatedDurationMin} Min.` : "",
    estimatedDurationMin: workOrder.estimatedDurationMin,
  }
}

const candidateIds = (context: AssignWorkOrderPreviewRowContext) =>
  Array.from(
    new Set(
      [context.selectedWorkOrderId, ...context.candidateWorkOrderIds].filter(
        (value): value is string => Boolean(value),
      ),
    ),
  )

const sourcePayload = (
  rawPayload: JsonObject,
  context: AssignWorkOrderPreviewRowContext | null,
  preview: AssignWorkOrderPreviewPayload | null,
  selectedWorkOrder: WorkOrderPreviewSummary | null,
) => {
  const origin = objectValue(rawPayload.origin)
  const presentation = objectValue(rawPayload.presentation)
  const propertyPayload = objectValue(rawPayload.property)
  const propertyAddress = objectValue(propertyPayload.address)
  const propertyUnit = objectValue(propertyPayload.unit)
  const requester = objectValue(rawPayload.requester)
  const requesterCustomer = objectValue(requester.customer)
  const requesterContact = objectValue(requester.contact)
  const suggestedContact = objectValue(requester.suggested_contact)
  const appointment = objectValue(rawPayload.appointment_request)
  const rawWorkOrder = objectValue(rawPayload.work_order)
  const property = propertyFor(
    context?.propertyId ?? selectedWorkOrder?.propertyId ?? null,
    preview ?? emptyPreview,
  )
  const event = scheduleEventFor(
    context?.proposalScheduleEventId ?? selectedWorkOrder?.currentScheduleEventId ?? null,
    preview ?? emptyPreview,
  )
  const selectedOrderTypeLabel =
    selectedWorkOrder && preview
      ? orderTypeLabelFor(selectedWorkOrder, preview)
      : selectedWorkOrder?.orderTypeLabel
  const selectedOrderTypes =
    selectedWorkOrder && preview ? orderTypeItemsFor(selectedWorkOrder, preview) : null
  const contactEmail = stringValue(requesterContact.email, suggestedContact.email)
  const contactPhone = stringValue(requesterContact.phone, suggestedContact.phone)
  const propertyUnitLabel =
    selectedWorkOrder?.propertyUnitLabel || stringValue(propertyUnit.label, propertyUnit.hint)

  return {
    ...rawPayload,
    source: origin.source,
    source_label: presentation.source_label,
    source_subline: presentation.source_subline,
    address_label: property?.addressLabel || propertyAddress.label || propertyAddress.freeform,
    property_label: property?.addressLabel || propertyPayload.label,
    property_id: context?.propertyId ?? selectedWorkOrder?.propertyId ?? propertyPayload.id,
    property_unit_id:
      context?.propertyUnitId ?? selectedWorkOrder?.propertyUnitId ?? propertyUnit.id,
    property_unit_label: propertyUnitLabel,
    unit_label: propertyUnitLabel,
    unit_hint: propertyUnit.hint,
    unit_verification: propertyUnit.verification,
    work_order: {
      ...rawWorkOrder,
      id: selectedWorkOrder?.id ?? rawWorkOrder.id,
      label: selectedWorkOrder
        ? [
            selectedOrderTypeLabel,
            selectedWorkOrder.orderNumber ? `#${selectedWorkOrder.orderNumber}` : "",
          ]
            .filter(Boolean)
            .join(" ")
        : rawWorkOrder.label,
      order_number: selectedWorkOrder?.orderNumber ?? rawWorkOrder.order_number,
      order_type_ids: selectedWorkOrder?.orderTypeIds ?? rawWorkOrder.order_type_ids,
      order_type_label: selectedOrderTypeLabel,
      order_types: selectedOrderTypes ?? rawWorkOrder.order_types,
      due_date: selectedWorkOrder?.dueDate ?? rawWorkOrder.due_date,
      estimated_duration_min: selectedWorkOrder?.estimatedDurationMin,
      property_id: selectedWorkOrder?.propertyId,
      property_unit_id: selectedWorkOrder?.propertyUnitId,
      property_unit_label: selectedWorkOrder?.propertyUnitLabel,
      assigned_employee_id: selectedWorkOrder?.assignedEmployeeId,
    },
    work_order_id: selectedWorkOrder?.id ?? rawWorkOrder.id,
    work_order_label: rawWorkOrder.label,
    appointment,
    requested_appointment: appointment,
    requested_time_label: appointment.requested_time_label,
    schedule_label: appointment.schedule_label,
    slot_start_at: appointment.slot_start_at,
    slot_end_at: appointment.slot_end_at,
    customer_window_start_at: appointment.customer_window_start_at,
    customer_window_end_at: appointment.customer_window_end_at,
    original_start_at: appointment.original_start_at,
    original_end_at: appointment.original_end_at,
    requested_appointment_decision: appointment.decision || appointment.requested_decision,
    blocker_employee_id: appointment.blocker_employee_id,
    target_employee_id: appointment.target_employee_id,
    target_employee_label: appointment.target_employee_label || appointment.target_employee_name,
    employee_changed: appointment.employee_changed,
    schedule_event: event
      ? {
          id: event.id,
          employee_id: event.employeeId,
          employee_name: event.employeeName,
          start_at: event.startAt,
          end_at: event.endAt,
          status: event.status,
          flex_type: event.flexType,
        }
      : rawPayload.schedule_event,
    schedule_event_id: event?.id,
    event_id: event?.id,
    employee_id: event?.employeeId || appointment.slot_employee_id,
    employee_label:
      event?.employeeName || appointment.slot_employee_label || appointment.slot_employee_name,
    customer_id: requesterCustomer.id,
    customer_name: requester.submitted_name || requesterCustomer.name || requesterCustomer.label,
    customer_role_hint:
      requester.submitted_role || requester.suggested_role || requesterCustomer.role_hint,
    contact_email: contactEmail,
    contact_phone: contactPhone,
    email: contactEmail,
    phone: contactPhone,
    visitor_message: requester.visitor_message,
    user_statement: requester.user_statement,
  } satisfies JsonObject
}

const emptyPreview: AssignWorkOrderPreviewPayload = {
  rowContextByActionCaseId: {},
  rowErrorsByActionCaseId: {},
  workOrdersById: {},
  propertiesById: {},
  scheduleEventsById: {},
  orderTypesById: {},
}

const durableOutputError = "Gespeicherte Auftragszuordnung fehlt oder ist ungültig."

const requesterPresentation = (payload: JsonObject) => {
  const requester = objectValue(payload.requester)
  const customer = objectValue(requester.customer)
  const contact = objectValue(requester.contact)
  const suggestedContact = objectValue(requester.suggested_contact)
  const name = stringValue(
    requester.submitted_name,
    customer.name,
    customer.label,
    "Ansprechpartner",
  )
  const role = roleLabel(requester.submitted_role || requester.suggested_role || customer.role_hint)
  const email = stringValue(contact.email, suggestedContact.email)
  const phone = stringValue(contact.phone, suggestedContact.phone)
  const contactMethods = [
    email
      ? {
          label: email,
          kind: "email" as const,
          tone: "info" as const,
          icon: mdiEmailOutline,
          tooltipContent: buildRichTooltip([
            tooltipTitle("E-Mail"),
            tooltipField("Adresse", email),
          ]),
        }
      : null,
    phone
      ? {
          label: phone,
          kind: "phone" as const,
          tone: "info" as const,
          icon: mdiPhoneOutline,
          tooltipContent: buildRichTooltip([
            tooltipTitle("Telefon"),
            tooltipField("Nummer", phone),
          ]),
        }
      : null,
  ].filter((value) => value !== null) as WorkOrderAssignmentRequesterContactMethod[]
  const tags: WorkOrderAssignmentContextTag[] = [
    {
      label: name,
      detail: role,
      tone: "neutral",
      icon: mdiAccountOutline,
      tooltipContent: buildRichTooltip([
        tooltipTitle("Ansprechpartner"),
        tooltipField("Name", name),
        tooltipField("Rolle", role),
      ]),
    },
    ...contactMethods,
  ]
  const notes = [
    stringValue(requester.visitor_message),
    stringValue(requester.user_statement),
    stringValue(objectValue(requester.customer_note).text),
    ...objectArray(requester.operator_notes).map((note) => stringValue(note.text)),
  ].filter(Boolean)

  return {
    name,
    role,
    email,
    contactMethods,
    tags,
    notes: Array.from(new Set(notes)),
  }
}

const requesterHintTags = (unitHint: string, notes: string[]): WorkOrderAssignmentContextTag[] => {
  const seen = new Set<string>()
  const tags: WorkOrderAssignmentContextTag[] = []
  const append = (label: string, detail: string, tone: WorkOrderAssignmentContextTag["tone"]) => {
    const normalizedDetail = detail.trim()
    const dedupeKey = normalizedDetail.toLocaleLowerCase("de-DE")

    if (!dedupeKey || seen.has(dedupeKey)) return

    seen.add(dedupeKey)
    tags.push({
      label,
      detail: normalizedDetail,
      detailAsSubLabel: true,
      tone,
      tooltipContent: buildRichTooltip([
        tooltipTitle(label),
        tooltipField("Hinweis", normalizedDetail),
      ]),
    })
  }

  append("Hinweis zur Wohneinheit", unitHint, "warning")
  for (const note of notes) append("Hinweis", note, "neutral")

  return tags
}

const buildRow = ({
  item,
  workflow,
  step,
  preview,
  rowOutcomes,
}: {
  item: InboxWorkflowItem
  workflow: InboxWorkflowState
  step: InboxWorkflowStep
  preview: AssignWorkOrderPreviewPayload | null
  rowOutcomes: InboxWorkflowRowOutcomeMap
}): WorkOrderAssignmentRow => {
  const actionCaseId = item.action_case.id
  const projectedStep = workflowItemStepFor(item, step.step_key)
  const output = workflowItemOutputFor(item, "assign_work_order")
  const context = preview?.rowContextByActionCaseId[actionCaseId] ?? null
  const previewReason =
    preview?.rowErrorsByActionCaseId[actionCaseId] ||
    (!preview ? "Auftragsvorschau fehlt." : !context ? "Auftragskontext fehlt." : "")
  const outputWorkOrderId = stringValue(output.work_order_id, output.selected_work_order_id) || null
  const needsStep = workflowItemNeedsStepFor(item, step)
  const resolved = workflowItemStepResolvedFor(item, step)
  const outputError = resolved && !outputWorkOrderId ? durableOutputError : null
  const activePreviewReason = needsStep ? previewReason : ""
  const selectedWorkOrderId = resolved
    ? outputWorkOrderId
    : (outputWorkOrderId ?? context?.selectedWorkOrderId ?? null)
  const selectedWorkOrder =
    selectedWorkOrderId && preview ? (preview.workOrdersById[selectedWorkOrderId] ?? null) : null
  const rawPayload = objectValue(item.action_case.payload)
  const payload = sourcePayload(rawPayload, context, preview, selectedWorkOrder)
  const candidates =
    context && preview
      ? candidateIds(context).map((id) => candidateFor(preview.workOrdersById[id]!, preview))
      : []
  const propertyId = context?.propertyId ?? selectedWorkOrder?.propertyId ?? null
  const property = propertyFor(propertyId, preview ?? emptyPreview)
  const requester = requesterPresentation(rawPayload)
  const appointment = objectValue(rawPayload.appointment_request)
  const proposalRef = rawPayload.appointment_proposal_ref
  const proposalEventId = stringValue(
    objectValue(proposalRef).schedule_event_id,
    context?.proposalScheduleEventId,
  )
  const proposalEvent = scheduleEventFor(proposalEventId || null, preview ?? emptyPreview)
  const selectedEvent = scheduleEventFor(
    selectedWorkOrder?.currentScheduleEventId ?? null,
    preview ?? emptyPreview,
  )
  const canceledEvent = scheduleEventFor(
    context?.canceledScheduleEventId ?? null,
    preview ?? emptyPreview,
  )
  const rowOutcome = rowOutcomes[actionCaseId] ?? null
  const statusOutcome =
    rowOutcome && ["success", "skipped", "failed"].includes(rowOutcome.status)
      ? {
          actionCaseId,
          stepKey: step.step_key,
          status: rowOutcome.status as "success" | "skipped" | "failed",
          label: rowOutcome.status,
          message: rowOutcome.error || rowOutcome.detail || "",
          hint: "",
        }
      : null
  const status = outputError
    ? {
        kind: "review" as const,
        label: "Datenfehler",
        detail: outputError,
        hint: "Die gespeicherte Entscheidung kann nicht zuverlässig dargestellt werden.",
        requiresAttention: true,
      }
    : workflowStatusFromRowState({
        outcome: statusOutcome,
        compatible: item.compatible && !activePreviewReason,
        open: needsStep,
        resolved,
        stepStatus: projectedStep?.status,
        error: activePreviewReason || item.error,
      })
  const unitLabel =
    selectedWorkOrder?.propertyUnitLabel ||
    stringValue(objectValue(objectValue(rawPayload.property).unit).label) ||
    "—"
  const appointmentTime = inboxAppointmentTimeFromPayload(payload)
  const scheduleLabel = inboxScheduleLabelFromPayload(payload) || "—"
  const workOrderLabel =
    candidates.find((candidate) => candidate.id === selectedWorkOrderId)?.label ||
    (resolved && selectedWorkOrderId
      ? `Auftrag ${selectedWorkOrderId}`
      : stringValue(objectValue(rawPayload.work_order).label)) ||
    "—"
  const targetEmployeeId =
    stringValue(
      appointment.target_employee_id,
      appointment.slot_employee_id,
      selectedWorkOrder?.assignedEmployeeId,
    ) || null
  const targetEmployeeLabel = stringValue(
    appointment.target_employee_label,
    appointment.target_employee_name,
    appointment.slot_employee_label,
    appointment.slot_employee_name,
    selectedEvent?.employeeName,
  )
  const rawUnit = objectValue(objectValue(rawPayload.property).unit)
  const unitHint = stringValue(rawUnit.hint)
  const hintTags = requesterHintTags(unitHint, requester.notes)
  const disabled =
    rowOutcome?.status === "success" ||
    !needsStep ||
    !item.compatible ||
    Boolean(activePreviewReason) ||
    Boolean(outputError)

  return {
    id: actionCaseId,
    actionCaseId,
    flowKey: workflow.flow.flow_key,
    workflowItem: item,
    step: projectedStep,
    appointmentProposalRef: proposalEventId ? { scheduleEventId: proposalEventId } : null,
    isCancellation: proposalRef === null,
    sourceLabel: stringValue(
      objectValue(rawPayload.presentation).source_label,
      item.action_case.source,
      objectValue(rawPayload.origin).source,
      "Inbox",
    ),
    addressLabel:
      property?.addressLabel ||
      stringValue(objectValue(objectValue(rawPayload.property).address).label, "—"),
    propertyHints: [],
    unitLabel,
    explicitUnitLabel: unitLabel === "—" ? "" : unitLabel,
    unitContextLabel: unitLabel,
    unitContextTags: [],
    customerLabel: requester.name,
    customerContextTags: requester.tags,
    customerContextNotes: requester.notes,
    requesterContact: {
      name: requester.name,
      roleLabel: requester.role,
      tooltipContent: buildRichTooltip([
        tooltipTitle("Ansprechpartner"),
        tooltipField("Name", requester.name),
        tooltipField("Rolle", requester.role),
      ]),
    },
    requesterContactMethods: requester.contactMethods,
    requesterHints: { tags: hintTags, notes: [] },
    scheduleLabel,
    appointmentTime,
    employeeLabel: proposalEvent?.employeeName || targetEmployeeLabel || "—",
    workOrderLabel,
    contactEmail: requester.email,
    statusLabel: status.label,
    statusDetail: status.detail,
    statusHint: status.hint,
    rowOutcome,
    workflowStatus: status,
    disabled,
    needsStep,
    actionCase: item.action_case,
    payload,
    candidates,
    preferredCandidates: propertyId
      ? candidates.filter((candidate) => candidate.propertyId === propertyId)
      : candidates,
    workOrderId: selectedWorkOrderId,
    scheduleEventId: proposalEventId || selectedWorkOrder?.currentScheduleEventId || null,
    canceledScheduleEvent: scheduleEventView(canceledEvent),
    blockerEmployeeId: stringValue(appointment.blocker_employee_id) || null,
    targetEmployeeId,
    targetEmployeeLabel: targetEmployeeLabel || "—",
    employeeChanged: Boolean(appointment.employee_changed),
    requestedAppointmentDecision:
      stringValue(appointment.decision, appointment.requested_decision) || null,
    propertyId,
    propertyUnitId: context?.propertyUnitId ?? selectedWorkOrder?.propertyUnitId ?? null,
    propertyUnitLabel: unitLabel === "—" ? "" : unitLabel,
    isOpen: needsStep,
    isResolved: resolved,
    isCompatible: item.compatible,
    searchText: [
      item.action_case.title,
      property?.addressLabel,
      unitLabel,
      requester.name,
      requester.email,
      unitHint,
      ...requester.notes,
      targetEmployeeLabel,
      workOrderLabel,
      ...candidates.map((candidate) => candidate.label),
    ]
      .filter(Boolean)
      .join(" "),
  }
}

export const buildWorkOrderAssignmentRows = ({
  workflow,
  step,
  rowOutcomes = {},
  stepPreview,
}: {
  workflow: InboxWorkflowState
  step: InboxWorkflowStep
  rowOutcomes?: InboxWorkflowRowOutcomeMap
  stepPreview: AssignWorkOrderPreviewPayload | null
}): WorkOrderAssignmentRow[] =>
  workflow.items
    .filter((item) => itemIsRelevant(item, workflow, step))
    .map((item) => buildRow({ item, workflow, step, preview: stepPreview, rowOutcomes }))
